import type React from "react";
import type { GalleryFilePreview } from "./types";
import { helperClass, labelClass, sectionCard } from "./constants";

type GallerySectionProps = {
  galleryImageUrls: string[];
  galleryImageFiles: File[];
  galleryFilePreviews: GalleryFilePreview[];
  handleGalleryFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeGalleryUrl: (url: string) => void;
  removeGalleryFile: (index: number) => void;
};

export const GallerySection = ({
  galleryImageUrls,
  galleryImageFiles,
  galleryFilePreviews,
  handleGalleryFileChange,
  removeGalleryUrl,
  removeGalleryFile,
}: GallerySectionProps) => (
  <section className={sectionCard}>
    <h2 className="text-xl font-semibold text-[#5c2f2f] mb-4">
      Product Gallery
    </h2>

    <div>
      <label className={labelClass}>Gallery Images</label>
      <label className="relative flex cursor-pointer flex-col gap-3 rounded-xl border border-dashed border-[#d7c0b7] bg-[#fbf8f6] p-4 hover:border-[#5c2f2f] sm:flex-row sm:items-center sm:justify-between">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleGalleryFileChange}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
          }}
          aria-label="Choose gallery images"
        />
        <span className="rounded-xl bg-[#5c2f2f] px-4 py-3 text-center text-sm font-semibold text-white">
          Choose images
        </span>
        <span className="text-sm text-gray-600">
          {galleryImageFiles.length === 0
            ? "No new images selected"
            : `${galleryImageFiles.length} new image${
                galleryImageFiles.length === 1 ? "" : "s"
              } selected`}
        </span>
      </label>
      {galleryImageFiles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {galleryImageFiles.map((file, index) => (
            <span
              key={`${file.name}-${file.lastModified}-${index}`}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700"
            >
              <span className="max-w-[220px] truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeGalleryFile(index)}
                className="font-bold text-red-600 hover:text-red-700"
                aria-label={`Remove ${file.name}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      )}
      <p className={helperClass}>
        Upload extra product photos. Use the x on any image to remove it from this
        product before saving.
      </p>
    </div>

    {galleryImageUrls.length === 0 && galleryImageFiles.length === 0 ? (
      <p className="mt-4 text-sm text-gray-500">No gallery images yet.</p>
    ) : (
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
        {galleryImageUrls.map((url) => (
          <div key={url} className="relative rounded-xl border bg-white p-2">
            <img
              src={url}
              alt="Gallery"
              className="h-32 w-full rounded-lg object-cover"
            />
            <button
              type="button"
              onClick={() => removeGalleryUrl(url)}
              className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white text-sm font-bold text-red-600 shadow hover:bg-red-50"
              aria-label="Remove gallery image"
            >
              x
            </button>
          </div>
        ))}

        {galleryFilePreviews.map(({ file, index, previewUrl }) => (
          <div
            key={`${file.name}-${file.lastModified}-${index}`}
            className="relative rounded-xl border bg-white p-2"
          >
            <img
              src={previewUrl}
              alt={file.name}
              className="h-32 w-full rounded-lg object-cover"
            />
            <button
              type="button"
              onClick={() => removeGalleryFile(index)}
              className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white text-sm font-bold text-red-600 shadow hover:bg-red-50"
              aria-label={`Remove ${file.name}`}
            >
              x
            </button>
          </div>
        ))}
      </div>
    )}
  </section>
);
